import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import LocalePickerGrid from "./LocalePickerGrid";

/**
 * Edit-shave dialog. Re-uses the same locale-picker as AssignDialog,
 * but changing the shave/cap re-runs the shave engine on already-
 * synced days for this pub × campaign (server cascades the update);
 * locale toggles affect only NEW tracker URLs going forward — we
 * never rewrite historical click attribution to a different locale.
 */
export default function EditAssignDialog({
  assignment, onClose,
  localeCatalog,
  shave, onShaveChange,
  passthrough, onPassthroughChange,
  locales, onToggleLocale,
  saving, onSubmit,
}) {
  return (
    <Dialog open={Boolean(assignment)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sn-edit-shave-dialog">
        <DialogHeader>
          <DialogTitle>Edit payout config & locales</DialogTitle>
          <DialogDescription>
            {assignment?.publisher_email} on <span className="font-mono">{assignment?.campaign_label}</span>.
            Changing shave/cap re-shaves already-synced days for this publisher.
            Locale changes apply to new tracker URLs only (historical clicks stay attributed to whatever locale they used).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Shave in $0.10–$0.27 zone</Label>
            <Select value={shave} onValueChange={onShaveChange}>
              <SelectTrigger data-testid="sn-edit-shave-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">No shave — full pass-through</SelectItem>
                <SelectItem value="0.01">$0.01 (1¢)</SelectItem>
                <SelectItem value="0.02">$0.02 (2¢)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start gap-2 border-t border-slate-100 pt-3">
            <Checkbox
              id="edit-passthrough"
              checked={passthrough}
              onCheckedChange={(v) => onPassthroughChange(Boolean(v))}
              data-testid="sn-edit-passthrough"
            />
            <div className="flex-1">
              <Label htmlFor="edit-passthrough" className="text-xs font-bold text-slate-700 cursor-pointer">
                Pass through above $0.27 (no cap)
              </Label>
              <p className="text-[10px] text-slate-500 mt-0.5">
                When checked, pub gets full feed EPC above $0.27 too. Unchecked = capped at $0.27.
              </p>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">
              Amazon storefronts ({locales.length} selected)
            </Label>
            <p className="text-[10px] text-slate-500 mb-2 mt-0.5">
              Toggle which Amazon locales this pub can route traffic to.
            </p>
            <LocalePickerGrid
              catalog={localeCatalog}
              selected={locales}
              onToggle={onToggleLocale}
              testIdPrefix="sn-edit"
            />
            {locales.length === 0 && (
              <p className="text-[10px] text-rose-600 mt-1">Pick at least one locale.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving || locales.length === 0} className="bg-[#002FA7] hover:bg-[#002089] text-white" data-testid="sn-edit-shave-save">
            {saving ? "Saving…" : "Save & re-shave"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
